<template>
  <main>
    <div class="row">
      <!-- Tabs status -->
      <div class="col-12">
        <b-tabs v-model="statusTabIndex" @input="onStatusTab">
          <b-tab :title="$t('all')"></b-tab>
          <b-tab>
            <template #title>
              <span class="log-dot" style="background: #28a745"></span> {{ $t('success') }}
            </template>
          </b-tab>
          <b-tab>
            <template #title>
              <span class="log-dot" style="background: #dc3545"></span> {{ $t('failed') }}
            </template>
          </b-tab>
        </b-tabs>
      </div>

      <!-- Filter -->
      <div class="col-12">
        <div class="tile">
          <div class="form-inline">
            <b-form-input class="mr-1 mb-1" v-model="filterFormModel.keyword" v-on:keydown.enter="search"
                          :placeholder="$t('keyword')" style="min-width: 220px"></b-form-input>
            <client-select :model="filterFormModel" class="mr-1 mb-1" attribute="client" @change="search"
                           :isFilter="true" :place-holder="$t('selectClient')"></client-select>
            <time-range-picker :model="filterFormModel" :label="$t('createTime')" class="mr-1 mb-1"></time-range-picker>
            <button @click="search" class="btn btn-primary mr-1 mb-1">{{ $t('search') }}</button>
            <button @click="reset" class="btn btn-secondary mr-1 mb-1">Reset</button>
          </div>
        </div>
      </div>

      <!-- Paging top -->
      <div class="col-12">
        <div class="c-paging clearfix">
          <div class="c-paging__count" v-if="itemCount">{{ $t('found', [itemCount]) }}</div>
          <div class="c-paging__right">
            <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                          v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize"
                          @input="load"></b-pagination>
          </div>
        </div>
      </div>

      <!-- Card list -->
      <div class="col-12" v-if="items.length">
        <div class="log-list">
          <div v-for="item in items" :key="item._id"
               class="log-card" :class="{ 'log-card--error': !item.success }"
               :style="{ borderLeftColor: item.success ? '#28a745' : '#dc3545' }"
               @click="showDetailModal(item)" role="button">
            <div class="log-card__time">
              <div class="log-time-main">{{ item.createTime | moment('DD/MM HH:mm:ss') }}</div>
              <div class="log-meta">{{ item.createTime | moment('YYYY') }}</div>
            </div>

            <div class="log-card__type">
              <span class="badge mr-1" :class="getMethodBadge(item.method)">{{ item.method || '—' }}</span>
              <div class="log-meta mt-1" v-if="invoiceCount(item) > 0">
                {{ invoiceCount(item) }} hoá đơn
              </div>
            </div>

            <div class="log-card__main">
              <div class="log-row-1" v-if="item.client">
                <font-awesome-icon icon="building" class="text-muted mr-1"/>
                <strong>{{ item.client.companyName || item.client.domain }}</strong>
                <span class="log-meta ml-2" v-if="item.client.domain">{{ item.client.domain }}</span>
              </div>
              <div class="log-row-2 log-url" v-if="item.endpoint">
                <code>{{ item.endpoint }}</code>
              </div>
              <div class="log-row-3" v-if="invoiceNumbers(item)">
                <font-awesome-icon icon="file-invoice" class="text-muted mr-1"/>
                <span class="log-meta">{{ invoiceNumbers(item) }}</span>
              </div>
              <div class="log-row-3 text-danger" v-if="item.errorMsg">
                <font-awesome-icon icon="exclamation-triangle" class="mr-1"/>
                {{ item.errorMsg }}
              </div>
            </div>

            <div class="log-card__status">
              <b-badge v-if="item.success" variant="success">{{ $t('success') }}</b-badge>
              <b-badge v-else variant="danger">{{ $t('failed') }}</b-badge>
            </div>

            <div class="log-card__chev"><font-awesome-icon icon="chevron-right"/></div>
          </div>
        </div>
      </div>

      <div class="col-12 text-center text-muted py-4" v-if="!items.length">
        <font-awesome-icon icon="inbox" size="3x" class="mb-2 d-block"/>
        <span v-if="filterFormModel.page === 1 && !filterFormModel.keyword">{{ $t('notFoundCreate') }}</span>
        <span v-else>{{ $t('notFoundSearch') }}</span>
      </div>

      <div class="col-12" v-if="itemCount > filterFormModel.pageSize">
        <div class="c-paging clearfix">
          <div class="c-paging__count">{{ $t('found', [itemCount]) }}</div>
          <div class="c-paging__right">
            <b-pagination class="pull-right" size="sm"
                          v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize"
                          @input="load"></b-pagination>
          </div>
        </div>
      </div>
    </div>

    <!-- Detail modal -->
    <b-modal v-model="showDetail" size="xl" hide-footer scrollable :modal-class="['log-modal']" no-close-on-backdrop no-enforce-focus>
      <template #modal-title>
        <span v-if="detailItem">
          <span class="badge" :class="getMethodBadge(detailItem.method)">{{ detailItem.method }}</span>
          <span class="ml-2">{{ $t('detail') }}</span>
        </span>
      </template>

      <div v-if="detailItem">
        <div class="log-meta-grid">
          <div class="log-meta-cell">
            <div class="log-meta-label">{{ $t('status') }}</div>
            <b-badge v-if="detailItem.success" variant="success">{{ $t('success') }}</b-badge>
            <b-badge v-else variant="danger">{{ $t('failed') }}</b-badge>
          </div>
          <div class="log-meta-cell">
            <div class="log-meta-label">{{ $t('method') }}</div>
            <div>{{ detailItem.method || '—' }}</div>
          </div>
          <div class="log-meta-cell">
            <div class="log-meta-label">{{ $t('createTime') }}</div>
            <div>{{ detailItem.createTime | moment('DD/MM/YYYY HH:mm:ss') }}</div>
          </div>
          <div class="log-meta-cell">
            <div class="log-meta-label">{{ $t('client') }}</div>
            <div v-if="detailItem.client">{{ detailItem.client.domain }}</div>
            <div v-else>—</div>
          </div>
        </div>

        <div class="log-section">
          <div class="log-section__label">Endpoint URL</div>
          <div class="log-url-block"><code>{{ detailItem.endpoint || '—' }}</code></div>
        </div>

        <div class="alert alert-danger mt-3" v-if="detailItem.errorMsg">
          <strong>{{ $t('error') }}:</strong> {{ detailItem.errorMsg }}
        </div>

        <div class="row mt-3">
          <div class="col-md-6">
            <div class="log-block">
              <div class="log-block__head">
                <span>Request</span>
                <button v-if="detailItem.requestData" @click="copy(JSON.stringify(detailItem.requestData))"
                        class="btn btn-sm btn-link p-0">
                  <font-awesome-icon icon="copy"/> Copy
                </button>
              </div>
              <div class="log-block__body">
                <json-viewer v-if="detailItem.requestData" theme="dark-json-viewer"
                             :value="detailItem.requestData" :expand-depth="20" :boxed="false" sort/>
                <div v-else class="text-muted">{{ $t('noData') }}</div>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="log-block" :class="{ 'log-block--err': !detailItem.success && detailItem.responseData }">
              <div class="log-block__head">
                <span>Response</span>
                <button v-if="detailItem.responseData" @click="copy(JSON.stringify(detailItem.responseData))"
                        class="btn btn-sm btn-link p-0">
                  <font-awesome-icon icon="copy"/> Copy
                </button>
              </div>
              <div class="log-block__body">
                <json-viewer v-if="detailItem.responseData" theme="dark-json-viewer"
                             :value="detailItem.responseData" :expand-depth="20" :boxed="false" sort/>
                <div v-else class="text-muted">{{ $t('noData') }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </main>
</template>

<script>
import List from '@/mixins/List';
import TimeRangePicker from "@/components/TimeRangePicker.vue";
import ClientSelect from "@/components/ClientSelect.vue";
import JsonViewer from 'vue-json-viewer';

export default {
  components: { TimeRangePicker, ClientSelect, JsonViewer },
  mixins: [List],
  data() {
    return {
      service: '/hilo-invoice-log',
      autoLoad: true,
      statusTabIndex: 0,
      defaultFilterFormModel: {
        keyword: null, client: null, status: null
      },
      showDetail: false,
      detailItem: null
    }
  },
  methods: {
    onStatusTab(idx) {
      this.filterFormModel.status = idx === 0 ? null : (idx === 1 ? '1' : '2');
      this.filterFormModel.page = 1;
      this.load();
    },
    invoiceCount(item) {
      return (item.invoices || []).length;
    },
    invoiceNumbers(item) {
      if (!item.invoices || !item.invoices.length) return '';
      return item.invoices.map(i => i.number || '(chưa cấp)').join(', ');
    },
    getMethodBadge(method) {
      if (method === 'GET') return 'badge-success';
      if (method === 'POST') return 'badge-primary';
      if (method === 'PUT') return 'badge-warning';
      if (method === 'DELETE') return 'badge-danger';
      return 'badge-secondary';
    },
    copy(str) {
      this.$copyText(str).then(() => {
        this.$swal({ text: 'Sao chép thành công!', icon: 'success', toast: true, position: 'top-end', timer: 3000 });
      });
    },
    search() {
      this.filterFormModel.page = 1;
      this.load();
    },
    load() {
      let loader = this.$loading.show();
      this.loading = true;
      this.listLoading = true;
      this.$service.get(this.service, { params: this.filterFormModel }).then(response => {
        this.apply(response.data);
      }).catch(error => {
        if (error.response && error.response.data.message) {
          this.$swal({ text: error.response.data.message, icon: 'error' });
        }
      }).finally(() => {
        loader.hide();
        this.loading = false;
        this.listLoading = false;
        if (this.afterLoad) this.afterLoad();
      });
    },
    showDetailModal(item) {
      this.detailItem = item;
      this.showDetail = true;
    }
  },
  i18n: {
    messages: {
      en: {
        all: 'All', invoice: 'Invoice', invoiceNumber: 'Invoice number',
        failed: 'Failed', method: 'Method', noData: 'No data'
      },
      vi: {
        all: 'Tất cả', invoice: 'Hoá đơn', invoiceNumber: 'Số hoá đơn',
        failed: 'Thất bại', method: 'Phương thức', noData: 'Không có dữ liệu'
      }
    }
  }
};
</script>

<style scoped>
.log-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 6px; vertical-align: middle; }
.log-list { display: flex; flex-direction: column; gap: 8px; }
.log-card {
  display: flex; align-items: flex-start; background: #fff;
  border: 1px solid #e8eaed; border-left: 4px solid #888; border-radius: 6px;
  padding: 12px 16px; cursor: pointer; transition: all 0.15s; gap: 16px;
}
.log-card:hover { border-color: #c9d1d9; box-shadow: 0 2px 8px rgba(0,0,0,0.06); transform: translateY(-1px); }
.log-card--error { background: #fef6f6; }

.log-card__time { min-width: 110px; flex-shrink: 0; }
.log-time-main { font-size: 13px; color: #333; font-weight: 500; }
.log-meta { font-size: 11px; color: #888; }

.log-card__type { min-width: 100px; flex-shrink: 0; }

.log-card__main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.log-row-1 { font-size: 14px; color: #222; }
.log-url { font-size: 12px; }
.log-url code { display: inline-block; max-width: 100%; overflow: hidden; text-overflow: ellipsis;
  white-space: nowrap; background: #f6f8fa; padding: 2px 6px; border-radius: 3px; color: #444; }
.log-row-3 { font-size: 12px; }

.log-card__status { display: flex; align-items: flex-start; padding-top: 2px; flex-shrink: 0; }
.log-card__chev { display: flex; align-items: center; color: #bbb; flex-shrink: 0; }

.log-meta-grid {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px;
  padding: 12px; background: #f8f9fa; border-radius: 6px;
}
.log-meta-label { font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
.log-section { margin-top: 20px; }
.log-section__label { font-size: 12px; color: #888; text-transform: uppercase; margin-bottom: 6px; }
.log-url-block { background: #f6f8fa; padding: 10px; border-radius: 4px; word-break: break-all; font-family: monospace; font-size: 13px; }
.log-block { border: 1px solid #e8eaed; border-radius: 6px; overflow: hidden; }
.log-block__head {
  display: flex; justify-content: space-between; align-items: center;
  padding: 8px 12px; background: #f6f8fa; font-weight: 600; font-size: 13px;
}
.log-block--err .log-block__head { background: #fde7e9; color: #c62828; }
.log-block__body { padding: 12px; background: #fff; max-height: 600px; overflow: auto; }
</style>
<style>.log-modal .modal-xl { max-width: 1280px; } .log-modal .dark-json-viewer { font-size: 12px; background: #f8f8fb; padding: 8px; }</style>
